import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { Checkbox } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import ContactCard from './components/ContactCard';
import { Contact } from 'models/Contact';
import ContactsContext from '../../../../providers/Contacts/Contacts.context';
import {
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
} from '../ContactsTable/components/ManagePeopleTableCells/data';
import MultiSort from 'components/MultiSort';
import { ASCENDING_DESCENDING_COPY, SORTING_ORDER } from 'models/Sort';

import './ContactsCards.styles.scss';

type ContactsCardsProps = {
  className?: string;
  isLoading?: boolean;
  contacts: Contact[];
  handleEdit: (contact: Contact) => void;
  handleDelete: (id: string) => void;
};

const ContactsCards: React.FC<ContactsCardsProps> = (props) => {
  const { className, contacts, handleEdit, handleDelete } = props;

  const classes = classNames('contacts-cards', className);

  const { t } = useTranslation();

  const { selected, isAllSelected, selectAll, removeAll, updateSelected } =
    useContext(ContactsContext);

  const hasSelectedItems = useMemo(
    () => !!selected?.length,
    [selected?.length],
  );

  const multiSortValues = useMemo(
    () =>
      Object.entries(SORTING_VALUES).map(([column, value]) => ({
        value,
        label: t(
          HEADING_TRANSLATIONS[column as keyof typeof HEADING_TRANSLATIONS] ||
            column,
        ),
      })),
    [t],
  );

  const multiSortDirectionTranslation = useMemo(
    () => ({
      [SORTING_VALUES.phone]: {
        [SORTING_ORDER.A_TO_Z]: t(
          ASCENDING_DESCENDING_COPY[SORTING_ORDER.A_TO_Z],
        ),
        [SORTING_ORDER.Z_TO_A]: t(
          ASCENDING_DESCENDING_COPY[SORTING_ORDER.Z_TO_A],
        ),
      },
      [SORTING_VALUES.preApproved]: {
        [SORTING_ORDER.A_TO_Z]: t('preApprovedLast'),
        [SORTING_ORDER.Z_TO_A]: t('preApprovedFirst'),
      },
    }),
    [t],
  );

  return (
    <div className={classes}>
      <div className="contacts-cards__select-all">
        <Checkbox
          label={t('selectAll')}
          checked={isAllSelected}
          onChange={isAllSelected ? removeAll : selectAll}
        />

        <MultiSort
          multiSortValues={multiSortValues}
          multiSortDirectionTranslation={multiSortDirectionTranslation}
        />
      </div>

      {contacts.map((contact) => (
        <ContactCard
          key={contact._id}
          contact={contact}
          isSelected={selected.some(({ _id }) => _id === contact._id)}
          handleEdit={() => handleEdit(contact)}
          handleDelete={() => handleDelete(contact._id)}
          handleOnSelect={() => updateSelected(contact)}
          handleOnClick={() => {
            if (hasSelectedItems) {
              updateSelected(contact);
            } else handleEdit(contact);
          }}
        />
      ))}
    </div>
  );
};

export default ContactsCards;
